import { BOARD_SIZE, getTileId, parseTileId } from '../boardConfig';
import { GAME_CONFIG } from '../config/gameConfig';
import { computeMoveScore } from './scoring';

const DIRECTIONS = {
  horizontal: { dr: 0, dc: 1 },
  vertical: { dr: 1, dc: 0 },
};

const buildPlacedTilesById = (committedBoard, stagedBoard) => {
  const placed = {};

  Object.entries(committedBoard).forEach(([tileId, tile]) => {
    placed[tileId] = tile;
  });

  Object.entries(stagedBoard).forEach(([tileId, tile]) => {
    placed[tileId] = tile;
  });

  return placed;
};

const walkUntilBoundary = (startRow, startCol, dr, dc, placedTilesById) => {
  let row = startRow;
  let col = startCol;

  while (true) {
    const nextRow = row - dr;
    const nextCol = col - dc;
    const nextId = getTileId(nextRow, nextCol);

    if (
      nextRow < 0 ||
      nextRow >= BOARD_SIZE ||
      nextCol < 0 ||
      nextCol >= BOARD_SIZE ||
      !placedTilesById[nextId]
    ) {
      break;
    }

    row = nextRow;
    col = nextCol;
  }

  return { row, col };
};

const collectWordFrom = (tileId, orientation, placedTilesById) => {
  const parsed = parseTileId(tileId);
  if (!parsed) {
    return null;
  }

  const { dr, dc } = DIRECTIONS[orientation];
  const start = walkUntilBoundary(parsed.row, parsed.col, dr, dc, placedTilesById);

  const cells = [];
  const letters = [];

  let row = start.row;
  let col = start.col;

  while (row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE) {
    const id = getTileId(row, col);
    const tile = placedTilesById[id];
    if (!tile) {
      break;
    }

    cells.push(id);
    letters.push(String(tile.letter || '').toUpperCase());

    row += dr;
    col += dc;
  }

  return {
    key: `${orientation}:${cells.join('|')}`,
    orientation,
    cells,
    text: letters.join(''),
  };
};

const hasSingleAxisPlacement = (stagedTileIds) => {
  const parsed = stagedTileIds.map((tileId) => parseTileId(tileId)).filter(Boolean);
  const rowSet = new Set(parsed.map((item) => item.row));
  const colSet = new Set(parsed.map((item) => item.col));

  return rowSet.size === 1 || colSet.size === 1;
};

const isPlacementContiguous = (stagedTileIds, placedTilesById) => {
  if (stagedTileIds.length <= 1) {
    return true;
  }

  const parsed = stagedTileIds.map((tileId) => parseTileId(tileId)).filter(Boolean);
  const sameRow = new Set(parsed.map((item) => item.row)).size === 1;

  if (sameRow) {
    const row = parsed[0].row;
    const cols = parsed.map((item) => item.col).sort((a, b) => a - b);
    const minCol = cols[0];
    const maxCol = cols[cols.length - 1];

    for (let col = minCol; col <= maxCol; col += 1) {
      if (!placedTilesById[getTileId(row, col)]) {
        return false;
      }
    }

    return true;
  }

  const col = parsed[0].col;
  const rows = parsed.map((item) => item.row).sort((a, b) => a - b);
  const minRow = rows[0];
  const maxRow = rows[rows.length - 1];

  for (let row = minRow; row <= maxRow; row += 1) {
    if (!placedTilesById[getTileId(row, col)]) {
      return false;
    }
  }

  return true;
};

const touchesCommittedTile = (stagedTileIds, committedBoard) =>
  stagedTileIds.some((tileId) => {
    const parsed = parseTileId(tileId);
    if (!parsed) {
      return false;
    }

    const neighbors = [
      getTileId(parsed.row + 1, parsed.col),
      getTileId(parsed.row - 1, parsed.col),
      getTileId(parsed.row, parsed.col + 1),
      getTileId(parsed.row, parsed.col - 1),
    ];

    return neighbors.some((neighborId) => Boolean(committedBoard[neighborId]));
  });

const getAllFormedWords = (stagedTileIds, placedTilesById) => {
  const wordMap = new Map();

  stagedTileIds.forEach((tileId) => {
    const horizontal = collectWordFrom(tileId, 'horizontal', placedTilesById);
    const vertical = collectWordFrom(tileId, 'vertical', placedTilesById);

    [horizontal, vertical]
      .filter(Boolean)
      .forEach((word) => {
        if (word.cells.length > 1 && !wordMap.has(word.key)) {
          wordMap.set(word.key, word);
        }
      });
  });

  return [...wordMap.values()];
};

const getMainWord = (words, stagedTileIds) => {
  if (!words.length) {
    return null;
  }

  if (stagedTileIds.length === 1) {
    const candidate = words
      .filter((word) => word.cells.length > 1)
      .sort((a, b) => b.cells.length - a.cells.length)[0];

    return candidate || words[0];
  }

  const parsed = stagedTileIds.map((tileId) => parseTileId(tileId)).filter(Boolean);
  const sameRow = new Set(parsed.map((item) => item.row)).size === 1;
  const targetOrientation = sameRow ? 'horizontal' : 'vertical';

  return (
    words
      .filter((word) => word.orientation === targetOrientation)
      .sort((a, b) => b.cells.length - a.cells.length)[0] || words[0]
  );
};

export const evaluateMove = ({
  committedBoard,
  stagedBoard,
  boardTiles,
  tileModifierOverrides,
  activeGameEffects = [],
}) => {
  const stagedTileIds = Object.keys(stagedBoard);
  const placedTilesById = buildPlacedTilesById(committedBoard, stagedBoard);
  const boardTileCount = Object.keys(committedBoard).length + stagedTileIds.length;
  const errors = [];

  if (!stagedTileIds.length) {
    return {
      hasTiles: false,
      isStructurallyValid: false,
      errors: ['Place at least one tile.'],
      words: [],
      scorePreview: 0,
      highlightByTileId: {},
      mainWord: null,
      wordsForDictionary: [],
    };
  }

  if (!hasSingleAxisPlacement(stagedTileIds)) {
    errors.push('Tiles must be placed in a single row or column.');
  }

  if (!isPlacementContiguous(stagedTileIds, placedTilesById)) {
    errors.push('Placed tiles must form one continuous line.');
  }

  const words = getAllFormedWords(stagedTileIds, placedTilesById);
  const mainWord = getMainWord(words, stagedTileIds);

  if (!mainWord) {
    errors.push('No playable word is formed.');
  }

  const isFirstMove = !Object.keys(committedBoard).length;
  const centerIncluded = mainWord?.cells.includes(GAME_CONFIG.centerTileId);

  if (isFirstMove && !centerIncluded) {
    errors.push('First move must cross the center tile.');
  }

  if (!isFirstMove && !touchesCommittedTile(stagedTileIds, committedBoard)) {
    errors.push('Move must connect to existing tiles.');
  }

  const wordsForDictionary = words.filter((word) => word.cells.length >= GAME_CONFIG.minWordLength);

  if (!wordsForDictionary.length) {
    errors.push('At least one word with 2 or more letters is required.');
  }

  const { totalScore, scoredWords } = computeMoveScore({
    words,
    boardTiles,
    tileModifierOverrides,
    placedTilesById,
    activeGameEffects,
    boardTileCount,
  });

  return {
    hasTiles: true,
    isStructurallyValid: errors.length === 0,
    errors,
    words: scoredWords,
    scorePreview: totalScore,
    highlightByTileId: {},
    mainWord,
    wordsForDictionary,
  };
};